import React from 'react';
import { AlertTriangle, RefreshCw, Home, ChevronDown, Copy } from 'lucide-react';

/**
 * Error Boundary 组件 — 朋际 v5.1
 * 
 * 捕获 React 渲染中的未处理错误，防止白屏。
 * 提供友好的错误展示、重试、返回首页功能。
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });

    // 开发环境打印到控制台
    if (process.env.NODE_ENV === 'development') {
      console.error('[ErrorBoundary] 捕获到错误:', error, errorInfo);
    }

    // 生产环境可上报到 Sentry
    if (process.env.NODE_ENV === 'production' && window.Sentry) {
      window.Sentry.captureException(error, {
        contexts: { react: { componentStack: errorInfo?.componentStack } },
      });
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null, showDetails: false });
  };

  handleCopyError = () => {
    const { error, errorInfo } = this.state;
    const text = [
      `错误: ${error?.message || '未知错误'}`,
      `堆栈: ${error?.stack || '无'}`,
      `组件: ${errorInfo?.componentStack || '无'}`,
      `时间: ${new Date().toISOString()}`,
      `URL: ${window.location.href}`,
    ].join('\n');

    navigator.clipboard.writeText(text).catch(() => {});
  };

  render() {
    if (this.state.hasError) {
      // 如果提供了自定义 fallback，优先使用
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const { error, showDetails } = this.state;

      return (
        <div className="min-h-screen flex items-center justify-center bg-neutral-50 dark:bg-dark-bg p-4">
          <div className="max-w-md w-full text-center">
            {/* 错误图标 */}
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center">
              <AlertTriangle className="w-10 h-10 text-red-500" />
            </div>

            {/* 标题 */}
            <h1 className="text-xl font-semibold text-neutral-800 dark:text-dark-text mb-2">
              {this.props.title || '页面出错了'}
            </h1>

            {/* 描述 */}
            <p className="text-neutral-500 dark:text-dark-muted mb-2">
              {this.props.message || '应用遇到了意外错误，请尝试刷新页面'}
            </p>

            {/* 错误详情（可折叠） */}
            <p className="text-sm text-neutral-400 dark:text-dark-muted/60 mb-6">
              {error?.message || '未知错误'}
            </p>

            {/* 错误详情展开 */}
            {this.state.errorInfo && (
              <div className="mb-6">
                <button
                  onClick={() => this.setState({ showDetails: !showDetails })}
                  className="inline-flex items-center gap-1 text-xs text-neutral-400 dark:text-dark-muted/60 hover:text-neutral-600 dark:hover:text-dark-muted mb-2 transition-colors"
                >
                  <ChevronDown
                    className={`w-3 h-3 transition-transform ${showDetails ? 'rotate-180' : ''}`}
                  />
                  技术详情
                </button>
                {showDetails && (
                  <div className="text-left bg-neutral-100 dark:bg-dark-card rounded-lg p-3 max-h-40 overflow-auto">
                    <pre className="text-xs text-neutral-600 dark:text-dark-muted whitespace-pre-wrap font-mono leading-relaxed">
                      {error?.stack}
                      {'\n\n'}
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </div>
                )}
              </div>
            )}

            {/* 操作按钮 */}
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={this.handleReset}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-xl text-sm font-medium transition-colors shadow-sm"
              >
                <RefreshCw className="w-4 h-4" />
                重试
              </button>

              <button
                onClick={() => { window.location.href = '/'; }}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-neutral-200 dark:bg-dark-border hover:bg-neutral-300 dark:hover:bg-dark-border/60 text-neutral-700 dark:text-dark-text rounded-xl text-sm font-medium transition-colors"
              >
                <Home className="w-4 h-4" />
                返回首页
              </button>

              <button
                onClick={this.handleCopyError}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-neutral-200 dark:bg-dark-border hover:bg-neutral-300 dark:hover:bg-dark-border/60 text-neutral-700 dark:text-dark-text rounded-xl text-sm font-medium transition-colors"
                title="复制错误信息"
              >
                <Copy className="w-4 h-4" />
                复制
              </button>
            </div>

            {/* 联系支持 */}
            {this.props.supportEmail && (
              <p className="mt-6 text-xs text-neutral-400 dark:text-dark-muted/60">
                如需帮助，请联系{' '}
                <a
                  href={`mailto:${this.props.supportEmail}`}
                  className="text-primary-500 hover:underline"
                >
                  {this.props.supportEmail}
                </a>
              </p>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * 带 ErrorBoundary 包裹的高阶组件
 */
export function withErrorBoundary(WrappedComponent, options = {}) {
  return function WithErrorBoundary(props) {
    return (
      <ErrorBoundary {...options}>
        <WrappedComponent {...props} />
      </ErrorBoundary>
    );
  };
}

export default ErrorBoundary;
